// Ciclo de vida da aba + armamento explícito do Offline Hunt.
//
// O Offline Hunt NÃO arma sozinho no pagehide. Só o botão "descansar" (ou um
// equivalente que chame armOfflineExit) carimba a saída no servidor. Fechar o
// jogo sem apertar não paga nada — modelo do Poke Idle World.
//
// Congelamento/descarte da aba pelo navegador continua sendo tratado à parte:
// se o Chrome Memory Saver matar a aba, apagamos qualquer carimbo local para
// não confundir com um descanso intencional.

import{markClientExitBeacon}from'../net/supabase-client.js?v=20260801-token-hash-1';

const EXIT_KEY=uid=>`idlemon-exit:${uid}`;
const REASON_KEY=uid=>`idlemon-exit-reason:${uid}`;
const FROZEN_KEY=uid=>`idlemon-frozen:${uid}`;

let userId='';
let keepAliveLock=null;
let keepAliveAudio=null;

const read=key=>{try{return localStorage.getItem(key)}catch{return null}};
const write=(key,value)=>{try{localStorage.setItem(key,value)}catch{}};
const drop=key=>{try{localStorage.removeItem(key)}catch{}};

function markFrozen(){
  if(!userId)return;
  drop(EXIT_KEY(userId));
  drop(REASON_KEY(userId));
  write(FROZEN_KEY(userId),'1');
}

/** Arma o offline no servidor. Sem isso, fechar o jogo não paga claim. */
export function armOfflineExit({reason='rest',atRisk=false,exitAt=Date.now()}={}){
  if(!userId)return false;
  const at=Number(exitAt)||Date.now();
  write(EXIT_KEY(userId),String(at));
  write(REASON_KEY(userId),String(reason||'rest'));
  drop(FROZEN_KEY(userId));
  return markClientExitBeacon(at,{reason,atRisk});
}

/** Consome o carimbo local de descanso (boot). Retorna {exitAt,reason} ou null. */
export function consumeCleanExitStamp(){
  if(!userId)return null;
  const frozen=read(FROZEN_KEY(userId))==='1';
  const raw=read(EXIT_KEY(userId));
  const reason=read(REASON_KEY(userId))||'rest';
  drop(EXIT_KEY(userId));
  drop(REASON_KEY(userId));
  drop(FROZEN_KEY(userId));
  if(frozen||document.wasDiscarded===true)return null;
  const at=Number(raw);
  if(!Number.isFinite(at)||at<=0||at>Date.now()+60_000)return null;
  return{exitAt:new Date(at).toISOString(),reason};
}

// Defesas contra o congelamento da aba (para o jogo REALMENTE rodar minimizado):
// 1. Web Lock mantido pela vida da página.
// 2. Áudio quase mudo em loop — só liga quando a hunt está ativa.
function startKeepAliveLock(){
  if(keepAliveLock||!navigator.locks?.request)return;
  keepAliveLock=navigator.locks.request('idlemon-alive',{mode:'exclusive'},()=>new Promise(()=>{}))
    .catch(()=>{keepAliveLock=null});
}

function silentLoopSource(){
  const rate=8000,frames=rate,bytes=44+frames*2,buffer=new ArrayBuffer(bytes),view=new DataView(buffer);
  const ascii=(offset,text)=>{for(let i=0;i<text.length;i++)view.setUint8(offset+i,text.charCodeAt(i))};
  ascii(0,'RIFF');view.setUint32(4,bytes-8,true);ascii(8,'WAVE');
  ascii(12,'fmt ');view.setUint32(16,16,true);view.setUint16(20,1,true);view.setUint16(22,1,true);
  view.setUint32(24,rate,true);view.setUint32(28,rate*2,true);view.setUint16(32,2,true);view.setUint16(34,16,true);
  ascii(36,'data');view.setUint32(40,frames*2,true);
  for(let i=0;i<frames;i++)view.setInt16(44+i*2,i%2?1:-1,true);
  let binary='';const raw=new Uint8Array(buffer);
  for(let i=0;i<raw.length;i++)binary+=String.fromCharCode(raw[i]);
  return`data:audio/wav;base64,${btoa(binary)}`;
}

export function setBackgroundKeepAlive(active){
  if(!active){
    keepAliveAudio?.pause();
    return;
  }
  if(!keepAliveAudio){
    keepAliveAudio=new Audio(silentLoopSource());
    keepAliveAudio.loop=true;
    keepAliveAudio.volume=.001;
  }
  keepAliveAudio.play().catch(()=>{});
}

export function initSessionLifecycle({userId:id,onResume}={}){
  userId=id||'';
  if(!userId)return;
  startKeepAliveLock();
  // pagehide NÃO arma offline — só o botão "descansar".
  const notifyResume=()=>{
    drop(FROZEN_KEY(userId));
    try{onResume?.()}catch(error){console.warn('resume handler failed',error)}
  };
  addEventListener('pageshow',(event)=>{
    // Voltou do bfcache/hibernação sem ter descansado de propósito: limpa
    // carimbo órfão e pede reconciliação autoritativa ao jogo.
    if(event.persisted||document.visibilityState==='visible')notifyResume();
  });
  addEventListener('freeze',markFrozen);
  addEventListener('resume',notifyResume);
}
